import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import API from '../../api/axiosConfig';
import { useAuth } from '../../context/AuthContext';

const StudentDashboard = () => {
    const navigate = useNavigate();
    const { userData } = useAuth();

    const [groups, setGroups] = useState([]);
    const [tests, setTests] = useState([]);
    const [classmates, setClassmates] = useState([]);
    const [activeGroup, setActiveGroup] = useState(null);
    const [activeTab, setActiveTab] = useState('tests');
    const [loading, setLoading] = useState(true);

    const [groupChat, setGroupChat] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [newChatMsg, setNewChatMsg] = useState('');
    const messagesEndRef = useRef(null);

    const fetchMyGroups = useCallback(async () => {
        try {
            const res = await API.get('/api/student/my-groups/');
            setGroups(res.data);
            if (res.data.length > 0 && !activeGroup) {
                setActiveGroup(res.data[0].id);
            }
        } catch (error) {
            console.error("Ошибка загрузки групп", error);
        } finally {
            setLoading(false);
        }
    }, [activeGroup]);

    useEffect(() => { fetchMyGroups(); }, [fetchMyGroups]);

    const fetchGroupData = useCallback(async () => {
        if (!activeGroup) return;
        try {
            const [testsRes, classmatesRes, chatsRes] = await Promise.all([
                API.get(`/api/student/groups/${activeGroup}/tests/`),
                API.get(`/api/student/groups/${activeGroup}/classmates/`),
                API.get('/api/chats/')
            ]);
            setTests(testsRes.data);
            setClassmates(classmatesRes.data);
            
            const chat = chatsRes.data.find(c => 
                (c.type === 'group' || c.room_type === 'group') && 
                (c.group_id === activeGroup || c.group === activeGroup)
            );
            if (chat) {
                setGroupChat(chat);
                const msgsRes = await API.get(`/api/chats/${chat.id}/messages/`);
                setChatMessages(msgsRes.data);
            }
        } catch (error) { console.error(error); }
    }, [activeGroup]);

    useEffect(() => { fetchGroupData(); }, [fetchGroupData]);

    useEffect(() => {
        if (activeTab === 'chat' && groupChat) {
            const interval = setInterval(async () => {
                try {
                    const res = await API.get(`/api/chats/${groupChat.id}/messages/`);
                    setChatMessages(res.data);
                } catch (error) {}
            }, 3000);
            return () => clearInterval(interval);
        }
    }, [activeTab, groupChat]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [chatMessages, activeTab]);

    const sendGroupMessage = async (e) => {
        e.preventDefault();
        if (!newChatMsg.trim() || !groupChat) return;
        try {
            await API.post(`/api/chats/${groupChat.id}/messages/`, { text: newChatMsg });
            setNewChatMsg('');
            const res = await API.get(`/api/chats/${groupChat.id}/messages/`);
            setChatMessages(res.data);
        } catch (error) { alert("Ошибка отправки"); }
    };

    const currentGroup = groups.find(g => g.id === activeGroup);

    if (loading) return <div className="flex justify-center min-h-screen items-center"><div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div></div>;

    return (
        <div className="min-h-screen bg-gray-50 p-4 font-sans relative">
            <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-5">
                
                {/* ЛЕВАЯ КОЛОНКА - СПИСОК ГРУПП */}
                <div className="lg:col-span-3 space-y-4">
                    <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex items-center justify-center">
                        <span className="font-black text-sm text-gray-800 uppercase tracking-widest">Мои классы</span>
                    </div>

                    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col h-[calc(100vh-140px)]">
                        <div className="overflow-y-auto flex-1 p-2 space-y-1 mt-2">
                            {groups.length === 0 && (
                                <div className="text-center p-6">
                                    <span className="text-3xl mb-2 block">🚷</span>
                                    <p className="text-xs text-gray-400 font-bold">Вы пока не состоите ни в одной группе. Учитель должен вас пригласить.</p>
                                </div>
                            )}
                            {groups.map(group => (
                                <div key={group.id} onClick={() => setActiveGroup(group.id)} className={`p-3 rounded-xl cursor-pointer transition-all border ${activeGroup === group.id ? 'bg-blue-50 border-blue-200' : 'bg-white border-transparent hover:bg-gray-50'}`}>
                                    <h3 className={`font-black text-sm mb-1 ${activeGroup === group.id ? 'text-blue-700' : 'text-gray-800'}`}>{group.name}</h3>
                                    <div className="flex justify-between items-center text-[10px] font-bold">
                                        <span className="text-gray-400">Преподаватель: {group.teacher_name}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ЦЕНТРАЛЬНАЯ КОЛОНКА */}
                <div className="lg:col-span-9 space-y-4">
                    {!currentGroup ? (
                        <div className="bg-white p-12 rounded-2xl border border-gray-200 text-center shadow-sm">
                            <span className="text-4xl block mb-4">📚</span>
                            <h2 className="text-xl font-black text-gray-800">Выберите группу слева, чтобы увидеть задания</h2>
                        </div>
                    ) : (
                        <>
                            {/* ШАПКА И ТАБЫ */}
                            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
                                <div className="flex justify-between items-end mb-4">
                                    <div>
                                        <h1 className="text-2xl font-black text-gray-900 uppercase tracking-tight">{currentGroup.name}</h1>
                                        <p className="text-xs text-gray-500 font-bold mt-1">Панель ученика</p>
                                    </div>
                                </div>

                                <div className="flex gap-2 bg-gray-50 p-1.5 rounded-xl border border-gray-100 overflow-x-auto">
                                    {[
                                        { id: 'tests', icon: '📝', label: 'Мои задания' },
                                        { id: 'chat', icon: '💬', label: 'Чат класса' },
                                        { id: 'classmates', icon: '👨‍🎓', label: 'Одноклассники' },
                                        { id: 'rating', icon: '🏆', label: 'Рейтинг' }
                                    ].map(tab => (
                                        <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-lg text-xs font-bold transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-white text-blue-600 shadow-sm border border-gray-200' : 'text-gray-500 hover:bg-gray-100'}`}>
                                            <span>{tab.icon}</span> {tab.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                                {/* ОСНОВНАЯ ЗОНА ТАБОВ */}
                                <div className="lg:col-span-3 bg-white rounded-2xl border border-gray-200 shadow-sm min-h-[500px] flex flex-col">
                                    
                                    {/* ТАБ ЗАДАНИЯ (ТЕСТЫ) */}
                                    {activeTab === 'tests' && (
                                        <div className="p-4">
                                            <h2 className="text-sm font-black text-gray-800 mb-6">Назначенные вам тесты</h2>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {tests.length === 0 && <p className="col-span-2 text-center text-gray-400 text-xs font-bold py-12 border-2 border-dashed rounded-xl">У вас пока нет новых заданий.</p>}
                                                {tests.map(test => {
                                                    const isOverdue = new Date(test.end_time) < new Date();
                                                    return (
                                                        <div key={test.id} className="p-5 bg-white border border-gray-200 rounded-xl shadow-sm hover:shadow-md transition-shadow flex flex-col">
                                                            <h3 className="font-black text-lg text-gray-800 mb-2">{test.title}</h3>
                                                            <p className="text-xs text-gray-500 mb-4 font-medium flex-1">{test.description}</p>
                                                            <div className="flex flex-col gap-2 mb-4 text-[11px] font-black">
                                                                <span className="bg-gray-100 text-gray-600 p-2 rounded-lg">📅 Доступен с: {new Date(test.start_time).toLocaleString('ru').slice(0, 17)}</span>
                                                                <span className={`p-2 rounded-lg ${isOverdue ? 'bg-red-100 text-red-600' : 'bg-orange-50 text-orange-600'}`}>
                                                                    🚨 Сдать до: {new Date(test.end_time).toLocaleString('ru').slice(0, 17)}
                                                                </span>
                                                            </div>
                                                            {/* === ЗДЕСЬ ОБНОВЛЕН МАРШРУТ (ПЕРЕДАЕМ ID СОБЫТИЯ) === */}
                                                            <button 
                                                                onClick={() => navigate(`/assigned-test/${test.id}`)} 
                                                                disabled={isOverdue}
                                                                className={`w-full py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-colors shadow-sm ${isOverdue ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700 text-white'}`}
                                                            >
                                                                {isOverdue ? 'Время вышло' : 'Начать тест'}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {/* ТАБ ЧАТ ГРУППЫ */}
                                    {activeTab === 'chat' && (
                                        <div className="h-full flex flex-col flex-1">
                                            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
                                                {!groupChat && <div className="text-center text-xs font-bold text-gray-400 my-auto pt-10">Чат загружается...</div>}
                                                {chatMessages.length === 0 && groupChat && <div className="text-center text-xs font-bold text-gray-400 my-auto pt-10">Поздоровайтесь с классом!</div>}
                                                
                                                {chatMessages.map((msg, idx) => {
                                                    const senderName = msg.sender_name || msg.sender_username || msg.sender || 'Пользователь';
                                                    const isMine = senderName === userData?.username || msg.sender === userData?.id;
                                                    
                                                    const prevMsg = chatMessages[idx - 1];
                                                    const prevSenderName = prevMsg ? (prevMsg.sender_name || prevMsg.sender_username || prevMsg.sender) : null;
                                                    const showName = idx === 0 || prevSenderName !== senderName;

                                                    return (
                                                        <div key={msg.id} className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}>
                                                            <div className={`max-w-[85%] ${isMine ? 'items-end' : 'items-start'} flex flex-col`}>
                                                                {showName && (
                                                                    <span className="text-[10px] font-bold text-gray-500 mx-1 mb-1">
                                                                        {isMine ? `Вы (${senderName})` : senderName}
                                                                    </span>
                                                                )}
                                                                <div className={`px-3 py-2 rounded-2xl shadow-sm text-xs font-medium ${isMine ? 'bg-blue-600 text-white rounded-br-sm' : 'bg-white text-gray-800 border border-gray-100 rounded-bl-sm'}`}>
                                                                    {msg.text}
                                                                </div>
                                                                <span className="text-[9px] font-semibold text-gray-400 mt-1 mx-1">
                                                                    {new Date(msg.created_at).toLocaleTimeString('ru', { hour: '2-digit', minute: '2-digit' })}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                <div ref={messagesEndRef} />
                                            </div>
                                            
                                            <div className="p-3 bg-white border-t border-gray-200 mt-auto">
                                                <form onSubmit={sendGroupMessage} className="flex gap-2">
                                                    <input type="text" placeholder="Написать в класс..." className="flex-1 bg-gray-100 border-none rounded-xl px-4 py-2 text-xs font-medium outline-none focus:ring-2 focus:ring-blue-500/20" value={newChatMsg} onChange={e => setNewChatMsg(e.target.value)} />
                                                    <button type="submit" disabled={!newChatMsg.trim()} className="bg-blue-600 disabled:bg-blue-300 hover:bg-blue-700 text-white rounded-xl px-4 py-2 text-xs font-bold transition-colors shadow-sm">➤</button>
                                                </form>
                                            </div>
                                        </div>
                                    )}

                                    {/* ТАБ ОДНОКЛАССНИКИ */}
                                    {activeTab === 'classmates' && (
                                        <div className="p-4">
                                            <h2 className="text-sm font-black text-gray-800 mb-4">Участники группы</h2>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                                {classmates.map(student => (
                                                    <div key={student.id} className="flex items-center gap-3 p-3 rounded-xl border bg-white border-gray-100 hover:shadow-sm transition-shadow">
                                                        <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-lg">👦</div>
                                                        <div>
                                                            <h3 className="text-sm font-bold text-gray-800">{student.name}</h3>
                                                            {student.id === userData?.id && <span className="text-[10px] font-black text-blue-600 uppercase tracking-widest bg-blue-50 px-2 py-0.5 rounded">Это вы</span>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* ТАБ РЕЙТИНГ */}
                                    {activeTab === 'rating' && (
                                        <div className="p-4">
                                            <h2 className="text-sm font-black text-gray-800 mb-4">Ваша успеваемость в классе</h2>
                                            <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 text-center">
                                                <span className="text-5xl block mb-2">🏅</span>
                                                <h3 className="text-xl font-black text-blue-900 mb-1">Рейтинг в разработке</h3>
                                                <p className="text-xs text-blue-700 font-medium">Здесь будет отображаться ваше место в классе и средний балл по результатам тестов.</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentDashboard;